// src/hooks/useStreamResponse.ts
import { useState, useCallback } from "react";
import { StreamResponse } from "../types";

export const useStreamResponse = (onChunk: (chunk: string) => void) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processStream = useCallback(
    async (response: Response) => {
      if (!response.body) return;

      setIsStreaming(true);
      setError(null);

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let pendingText = "";

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // Process any remaining text in the buffer
            if (pendingText.trim()) {
              try {
                const line = pendingText.trim();
                if (line.startsWith("data: ")) {
                  const jsonStr = line.slice(6);
                  const chunk = JSON.parse(jsonStr) as StreamResponse;
                  if (chunk.data) onChunk(chunk.data);
                }
              } catch (e) {
                console.error("Error processing final chunk:", e);
              }
            }
            break;
          }

          // Decode the current chunk and add to pending text
          const textChunk = decoder.decode(value, { stream: true });
          pendingText += textChunk;

          // Split by newlines and process complete lines
          const lines = pendingText.split("\n");

          // Keep the last (potentially incomplete) line in pending
          pendingText = lines.pop() || "";

          // Process all complete lines
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith("data: ")) {
              try {
                const jsonStr = trimmedLine.slice(6);
                const chunk = JSON.parse(jsonStr) as StreamResponse;

                if (chunk.error) {
                  setError(chunk.error);
                  return;
                }

                if (chunk.data) {
                  onChunk(chunk.data);
                }
              } catch (e) {
                console.error("Error parsing line:", trimmedLine, e);
              }
            }
          }
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "An error occurred while streaming"
        );
      } finally {
        setIsStreaming(false);

        // Clean up the decoder
        decoder.decode(new Uint8Array(0), { stream: false });
      }
    },
    [onChunk]
  );

  return { isStreaming, error, processStream };
};
